/**
 * RadialProgressChart — Recharts-backed radial bar gauge for percentage
 * progress reads.
 *
 * Pairs with the SVG-only {@link GaugeMeter} (single needle, threshold
 * bands) by offering a Recharts surface that interpolates animated
 * arcs and inherits the rest of the chart family's responsive
 * container, tooltip, and theming behavior.
 *
 * Single responsibility: render the radial gauge and surface its
 * value. Multi-series stacks, gauges, and chart-level interactivity
 * stay out of scope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from "recharts";
import { GRAY, PRIMARY, SAFE, WARN } from "../../design-system/tokens.js";

export type RadialProgressTone = "info" | "safe" | "warn" | "neutral";

export interface RadialProgressChartProps {
  /** Current value, in the units of `max`. */
  readonly value: number;
  /** Maximum value. Defaults to 100. */
  readonly max?: number;
  /** Visual tone. Defaults to `info`. */
  readonly tone?: RadialProgressTone;
  /** Pixel diameter. Defaults to 180. */
  readonly size?: number;
  /** Optional label rendered in the centre. */
  readonly label?: string;
  /**
   * Optional value formatter. Receives the clamped value and returns
   * the string rendered inside the ring. Defaults to a percentage
   * formatter relative to `max`.
   */
  readonly format?: (value: number, max: number) => string;
  /** Accessible label. Defaults to a value-derived string. */
  readonly ariaLabel?: string;
}

const TONE_FILL: Readonly<Record<RadialProgressTone, string>> = {
  info: PRIMARY[600],
  safe: SAFE[500],
  warn: WARN[500],
  neutral: GRAY[600],
};

function defaultFormat(value: number, max: number): string {
  if (max <= 0) return "0%";
  return `${Math.round((value / max) * 100)}%`;
}

/** Renders an animated radial progress arc with a centered value. */
export const RadialProgressChart = React.memo(function RadialProgressChart({
  value,
  max = 100,
  tone = "info",
  size = 180,
  label,
  format,
  ariaLabel,
}: RadialProgressChartProps): React.JSX.Element {
  const clamped = Math.max(0, Math.min(max, value));
  const formatter = format ?? defaultFormat;
  const data = React.useMemo(
    () => [{ name: "value", value: clamped, fill: TONE_FILL[tone] }],
    [clamped, tone],
  );
  const resolvedAriaLabel = ariaLabel ?? `${label ?? "Progress"}: ${formatter(clamped, max)}`;
  const formatted = formatter(clamped, max);

  return (
    <div
      role="img"
      aria-label={resolvedAriaLabel}
      data-csk-chart="radial-progress"
      data-csk-radial-tone={tone}
      style={{ width: size, height: size, position: "relative" }}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        initialDimension={{ width: size, height: size }}
      >
        <RadialBarChart
          innerRadius="72%"
          outerRadius="100%"
          barSize={12}
          data={data}
          startAngle={90}
          endAngle={-270}
        >
          <PolarAngleAxis type="number" domain={[0, max]} angleAxisId={0} tick={false} />
          <RadialBar
            background={{ fill: GRAY[300] }}
            dataKey="value"
            cornerRadius={8}
            isAnimationActive
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div
        style={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          inset: 0,
          justifyContent: "center",
          pointerEvents: "none",
          position: "absolute",
        }}
      >
        <span
          style={{
            color: GRAY[900],
            fontFamily: "ui-sans-serif, system-ui, sans-serif",
            fontSize: Math.max(16, size * 0.18),
            fontVariantNumeric: "tabular-nums",
            fontWeight: 700,
          }}
        >
          {formatted}
        </span>
        {label !== undefined && (
          <span
            style={{
              color: GRAY[600],
              fontSize: Math.max(10, size * 0.07),
              letterSpacing: "0.05em",
              marginTop: 2,
              textTransform: "uppercase",
            }}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
});
